import { apiClient as api, resolveTenantAlias } from "./httpClient";
import { ContractStanding } from "./contractsService";

export interface EntityUser {
  ueprId: string;
  userId?: string;
  name?: string;
  email?: string;
  professionalTypeId?: string;
  professionalTypeName?: string;
  profileIds: string[];
  profileName?: string;
  teamIds?: string[];
  userNickName?: string;
  document?: string;
  documentProfessional?: string;
  phoneCode?: string;
  phoneNumber?: string;
  /** DDI só com dígitos; nulo = Brasil (55) */
  phoneDdi?: string;
  cnpj?: string;
  razaoSocial?: string;
  inscricaoMunicipal?: string;
  addressZipCode?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressDistrict?: string;
  addressCity?: string;
  addressState?: string;
  bankCode?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountDigit?: string;
  bankPix?: string;
  active: boolean;
  /** usuário fixo do sistema (Sininho, conta de smoke): só ativar/inativar */
  isProtected?: boolean;
  /** situação do contrato do profissional (RN-558e); nulo = sessão sem a permissão de contratos */
  contract?: ContractStanding | null;
}

export interface ProvisionResult {
  ueprId: string;
  userId: string;
  fullName: string;
  email: string;
  isNewUser: boolean;
  inviteLink: string | null;
}

export interface OptionItem {
  id: string;
  name: string;
  isAdmin?: boolean;
  isProfessional?: boolean;
}

export interface BankOption {
  code: string;
  name: string;
}

export interface EntityUserFormOptions {
  professionalTypes: OptionItem[];
  profiles: OptionItem[];
  banks: BankOption[];
  teams: OptionItem[];
  /** a clínica tem canal de WhatsApp próprio; sem ele o convite por WhatsApp não aparece */
  hasWhatsAppChannel?: boolean;
}

export interface EntityUserPayload {
  fullName: string;
  userNickName?: string;
  document?: string;
  documentProfessional?: string;
  professionalTypeId?: string;
  profileIds?: string[];
  phoneCode?: string;
  phoneNumber?: string;
  phoneDdi?: string;
  cnpj?: string;
  razaoSocial?: string;
  inscricaoMunicipal?: string;
  addressZipCode?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressDistrict?: string;
  addressCity?: string;
  addressState?: string;
  bankCode?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountDigit?: string;
  bankPix?: string;
  active?: boolean;
}

export interface PasswordInviteOutcome {
  inviteLink: string | null;
  emailSent: boolean;
  whatsappSent: boolean;
  whatsappError: string | null;
}

export const entityUserService = {
  getByEntity: async (entityId: string): Promise<EntityUser[]> => {
    const response = await api.get(`/entity-users/by-entity/${entityId}`);
    return Array.isArray(response.data) ? response.data : [];
  },
  getFormOptions: async (entityId: string): Promise<EntityUserFormOptions> => {
    const response = await api.get(`/entity-users/form-options/${entityId}`);
    return response.data;
  },
  provision: async (
    entityId: string,
    email: string
  ): Promise<ProvisionResult> => {
    const response = await api.post(`/entity-users/provision`, {
      entityId,
      email,
      clinicAlias: resolveTenantAlias(),
    });
    return response.data;
  },
  // Reenvio do convite de senha por email e/ou WhatsApp (RN-005b / RN-005d). O link sempre volta:
  // e o plano B quando nenhum canal entrega (homolog, provedor fora, numero errado).
  resendPasswordInvite: async (
    userId: string,
    entityId: string,
    channels: { sendEmail: boolean; sendWhatsApp: boolean },
  ): Promise<PasswordInviteOutcome> => {
    const response = await api.post(`/entity-users/${userId}/resend-password-invite`, {
      entityId,
      clinicAlias: resolveTenantAlias(),
      sendEmail: channels.sendEmail,
      sendWhatsApp: channels.sendWhatsApp,
    });
    return {
      inviteLink: response.data?.inviteLink ?? null,
      emailSent: Boolean(response.data?.emailSent),
      whatsappSent: Boolean(response.data?.whatsappSent),
      whatsappError: response.data?.whatsappError ?? null,
    };
  },
  update: async (id: string, data: EntityUserPayload): Promise<void> => {
    await api.put(`/entity-users/${id}`, data);
  },
  setStatus: async (id: string, active: boolean): Promise<void> => {
    await api.patch(`/entity-users/${id}/status`, { active });
  },
  updateProfiles: async (
    id: string,
    profileIds: string[],
    teamIds: string[]
  ): Promise<void> => {
    // equipes ficam fora por ora: professional_teams tem o Bubble como master até o módulo migrar
    await api.put(`/entity-users/${id}/profiles`, { profileIds });
  },
};
